


import React, { useState, useEffect, useRef } from 'react';
import {
    Button, Form, Input, InputNumber, message, Card, Select, Upload,
    Row, Col, Space, Checkbox, Typography, Table, Popconfirm, Tooltip,
    Divider, Tabs, Alert
} from 'antd';
import {
    TrophyOutlined, DeleteOutlined, EyeOutlined, UploadOutlined,
    DownloadOutlined, SaveOutlined, StarOutlined, FireOutlined,
    CrownOutlined, SafetyCertificateOutlined, BgColorsOutlined,
    PictureOutlined, SettingOutlined, ReloadOutlined, EditOutlined,
    ExperimentOutlined, FormatPainterOutlined, ColumnWidthOutlined,
} from '@ant-design/icons';
import { Editor } from "@tinymce/tinymce-react";
import html2canvas from "html2canvas";
import { QRCodeCanvas } from "qrcode.react";
import courseService from '../../services/course.service';

const { Text } = Typography;

const BadgeManager = ({ courseId }) => {
    const badgeRef = useRef(null);
    const [threshold, setThreshold] = useState(10);
    const [badges, setBadges] = useState([]);
    const [loadingBadges, setLoadingBadges] = useState(false);
    const [courseTitle, setCourseTitle] = useState('');
    const [score, setScore] = useState(0);
    const [loading, setLoading] = useState(false);
    const [savingThreshold, setSavingThreshold] = useState(false);
    const [loadingSave, setLoadingSave] = useState(false);
    const [allUsers, setAllUsers] = useState([]);
    const [logoPreview, setLogoPreview] = useState(null);
    const [backgroundPreview, setBackgroundPreview] = useState(null);
    const [backgroundColor, setBackgroundColor] = useState("#1890ff");
    const [textColor, setTextColor] = useState("#ffffff");
    const [useImageBackground, setUseImageBackground] = useState(false);
    const [badgeWidth, setBadgeWidth] = useState(300);
    const [badgeHeight, setBadgeHeight] = useState(420);
    const [logoWidth, setLogoWidth] = useState(60);
    const [logoHeight, setLogoHeight] = useState(60);
    const [badgeHtmlContent, setBadgeHtmlContent] = useState("");
    const [qrLink, setQrLink] = useState("https://nomidya.com");
    const [verificationCode, setVerificationCode] = useState(null);
    const [loadingAutoCheck, setLoadingAutoCheck] = useState(false);
    const [activeTab, setActiveTab] = useState("design");
    const [templateLoaded, setTemplateLoaded] = useState(false);

    const [studentName, setStudentName] = useState(() => {
        const user = JSON.parse(localStorage.getItem("user"));
        return user?.username || "";
    });

    // ══════════ TOUS LES useEffect — INCHANGÉS ══════════
    useEffect(() => {
        if (templateLoaded) return;
        setBadgeHtmlContent(`
            <div style="text-align: center; height: 100%; display: flex; flex-direction: column; justify-content: center;">
                <div>
                    <h2 style="margin: 10px 0; font-size: 18px; font-weight: bold;">🏆 BADGE DE RÉUSSITE</h2>
                    <div style="margin: 20px 0;">
                        <div style="font-size: 14px; margin-bottom: 8px;">Décerné à :</div>
                        <div style="font-size: 16px; font-weight: bold; text-transform: uppercase; border-bottom: 2px solid currentColor; padding-bottom: 5px; margin: 0 20px;">\${studentName}</div>
                    </div>
                    <div style="margin: 20px 0;">
                        <div style="font-size: 12px; margin-bottom: 5px;">Pour le cours :</div>
                        <div style="font-size: 14px; font-weight: 600; font-style: italic;">\${courseTitle}</div>
                    </div>
                    <div style="margin: 25px 0;">
                        <div style="font-size: 24px; margin-bottom: 5px;">⭐</div>
                        <div style="font-size: 12px;">Score : \${score}/\${threshold}</div>
                    </div>
                </div>
            </div>
        `);
    }, [studentName, courseTitle, score, threshold, templateLoaded]);

    useEffect(() => {
        const fetchVerificationCode = async () => {
            if (studentName && courseId) {
                try {
                    const code = await courseService.getBadgeVerificationCode(studentName, courseId);
                    setVerificationCode(code);
                    setQrLink(`https://nomidya.com/badge/verify/${code}`);
                } catch (err) { setVerificationCode(null); setQrLink("https://nomidya.com/badge/verify/PENDING"); }
            }
        };
        fetchVerificationCode();
    }, [studentName, courseId]);

    const fetchBadges = async () => {
        if (courseId) {
            try { setLoadingBadges(true); const res = await courseService.getBadgesByCourse(courseId); setBadges(res); }
            catch (err) { console.error("Erreur chargement badges:", err); }
            finally { setLoadingBadges(false); }
        }
    };
    useEffect(() => { fetchBadges(); }, [courseId]);
    useEffect(() => { const f = async () => { try { setAllUsers(await courseService.getAllUsernames()); } catch (e) {} }; f(); }, []);
    useEffect(() => {
        const f = async () => {
            if (courseId) { try { const c = await courseService.getCourseDetails(courseId); setCourseTitle(c.title || ''); setThreshold(await courseService.getThreshold(courseId)); } catch (e) {} }
        }; f();
    }, [courseId]);

    useEffect(() => {
        if (!courseId) return;
        const loadTemplate = async () => {
            try {
                const template = await courseService.getLastBadgeTemplate(courseId);
                if (template) {
                    if (template.templateHtml) { setBadgeHtmlContent(template.templateHtml); setTemplateLoaded(true); }
                    if (template.threshold) setThreshold(template.threshold);
                    if (template.templateSettings) {
                        try {
                            const s = JSON.parse(template.templateSettings);
                            if (s.backgroundColor) setBackgroundColor(s.backgroundColor);
                            if (s.textColor) setTextColor(s.textColor);
                            if (s.logoWidth) setLogoWidth(s.logoWidth);
                            if (s.logoHeight) setLogoHeight(s.logoHeight);
                            if (s.badgeWidth) setBadgeWidth(s.badgeWidth);
                            if (s.badgeHeight) setBadgeHeight(s.badgeHeight);
                            if (s.useImageBackground !== undefined) setUseImageBackground(s.useImageBackground);
                            if (s.backgroundPreview) setBackgroundPreview(s.backgroundPreview);
                            if (s.logoPreview) setLogoPreview(s.logoPreview);
                        } catch (e) {}
                    }
                }
            } catch (e) {}
        };
        loadTemplate();
    }, [courseId]);

    // ══════════ FONCTIONS — INCHANGÉES ══════════
    const handleImageUpload = (info, setState) => {
        const file = info.file?.originFileObj || info.file;
        const reader = new FileReader();
        reader.onload = () => {
            const img = new Image();
            img.onload = () => {
                const canvas = document.createElement('canvas');
                const maxSize = 400;
                let w = img.width, h = img.height;
                if (w > h) { if (w > maxSize) { h = h * maxSize / w; w = maxSize; } }
                else { if (h > maxSize) { w = w * maxSize / h; h = maxSize; } }
                canvas.width = w; canvas.height = h;
                canvas.getContext('2d').drawImage(img, 0, 0, w, h);
                setState(canvas.toDataURL('image/jpeg', 0.7));
            };
            img.src = reader.result;
        };
        reader.readAsDataURL(file);
    };

    const generateBadgeBlob = async () => {
        try { const canvas = await html2canvas(badgeRef.current, { backgroundColor: null, scale: 2 }); return new Promise(r => canvas.toBlob(b => r(b), "image/png")); }
        catch (e) { message.error("Erreur génération."); }
    };

    const handleSaveBadgeTemplate = async () => {
        try {
            setLoadingSave(true);
            const blob = await generateBadgeBlob(); if (!blob) return;
            const file = new File([blob], `badge_template_${courseTitle}.png`, { type: "image/png" });
            const user = JSON.parse(localStorage.getItem("user"));
            const settings = { backgroundColor, textColor, logoWidth, logoHeight, useImageBackground, backgroundPreview, badgeWidth, badgeHeight, threshold, logoPreview };
            await courseService.uploadBadgeTemplate(file, courseId, user?.username || "Formateur", badgeHtmlContent, settings);
            message.success("✅ Template sauvegardé !");
        } catch (e) { message.error("Erreur sauvegarde."); }
        finally { setLoadingSave(false); }
    };

    const handleAutoCheckBadge = async () => {
        if (!studentName || !courseId) { message.warning("Sélectionnez un étudiant."); return; }
        try {
            setLoadingAutoCheck(true);
            const user = JSON.parse(localStorage.getItem("user"));
            const result = await courseService.checkAndGenerateBadge(courseId, user.id);
            if (result.status === "SUCCESS" || result.status === "ALREADY_EXISTS") {
                if (result.verificationCode) { setVerificationCode(result.verificationCode); setQrLink(`https://nomidya.com/badge/verify/${result.verificationCode}`); window.open(`/badge/verify/${result.verificationCode}`, "_blank"); }
                message.success(result.status === "SUCCESS" ? "🎖️ Badge généré !" : "ℹ️ Badge existant");
                await fetchBadges();
            } else { message.warning(result.message || "Erreur"); }
        } catch (e) { message.warning(e.response?.data?.message || "Erreur vérification."); }
        finally { setLoadingAutoCheck(false); }
    };

    const handleGenerate = async () => {
        if (!studentName || !courseTitle) { message.warning("Champs manquants."); return; }
        if (score < threshold) { message.warning(`Score insuffisant (${score}/${threshold}).`); return; }
        try {
            setLoading(true);
            const blob = await generateBadgeBlob(); if (!blob) return;
            const driveUrl = await courseService.getBadgeUrl(studentName.trim().split(" ")[0], parseInt(courseId), courseTitle, parseInt(score), parseInt(threshold));
            if (driveUrl) { window.open(driveUrl, "_blank"); message.success("🎖️ Badge généré !"); await fetchBadges();
                try { const code = await courseService.getBadgeVerificationCode(studentName, courseId); setVerificationCode(code); setQrLink(`https://nomidya.com/badge/verify/${code}`); } catch (e) {}
            } else { message.error("Badge non généré."); }
        } catch (e) { message.error("Erreur génération."); }
        finally { setLoading(false); }
    };

    const downloadBadge = async () => {
        try { const canvas = await html2canvas(badgeRef.current, { backgroundColor: null, scale: 3 }); const link = document.createElement('a'); link.download = `${studentName}_badge.png`; link.href = canvas.toDataURL(); link.click(); message.success("Téléchargé !"); }
        catch (e) { message.error("Erreur."); }
    };

    // ══════════════════════════════════════════════════
    // ✅ RENDU AMÉLIORÉ — UNIQUEMENT LE JSX
    // ══════════════════════════════════════════════════
    return (
        <div style={{ padding: '12px' }}>
            {/* ═══ HEADER ═══ */}
            <div style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                borderRadius: 12, padding: '14px 20px', marginBottom: 14,
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                flexWrap: 'wrap', gap: 8,
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <CrownOutlined style={{ fontSize: 24, color: '#faad14' }} />
                    <div>
                        <div style={{ color: 'white', fontSize: 16, fontWeight: 700 }}>Gestion des Badges</div>
                        <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 11 }}>{courseTitle}</div>
                    </div>
                </div>
                <Space size={6}>
                    <Button icon={<SaveOutlined />} loading={loadingSave} onClick={handleSaveBadgeTemplate} size="small"
                        style={{ borderRadius: 6, background: '#52c41a', color: 'white', border: 'none', fontWeight: 600, fontSize: 12 }}>
                        Sauvegarder
                    </Button>
                    <Button icon={<DownloadOutlined />} onClick={downloadBadge} size="small"
                        style={{ borderRadius: 6, background: 'rgba(255,255,255,0.15)', color: 'white', border: '1px solid rgba(255,255,255,0.25)', fontSize: 12 }}>
                        Télécharger
                    </Button>
                </Space>
            </div>

            <Row gutter={12}>
                {/* ═══ GAUCHE ═══ */}
                <Col span={24} lg={14}>
                    <Tabs activeKey={activeTab} onChange={setActiveTab} size="small"
                        items={[
                            {
                                key: 'design',
                                label: <span><FormatPainterOutlined /> Design</span>,
                                children: (
                                    <Card size="small" style={{ borderRadius: 8, border: '1px solid #e8e8e8' }}>
                                        {/* Étudiant / Cours */}
                                        <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                                            <div style={{ flex: 1 }}>
                                                <Text style={{ fontSize: 11, color: '#888', display: 'block', marginBottom: 2 }}>👤 Étudiant</Text>
                                                <Select showSearch placeholder="Choisir" value={studentName} onChange={setStudentName} size="small" style={{ width: '100%' }}
                                                    filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}>
                                                    {allUsers.map(u => <Select.Option key={u} value={u}>{u}</Select.Option>)}
                                                </Select>
                                            </div>
                                            <div style={{ flex: 1 }}>
                                                <Text style={{ fontSize: 11, color: '#888', display: 'block', marginBottom: 2 }}>📘 Cours</Text>
                                                <Input value={courseTitle} readOnly size="small" />
                                            </div>
                                        </div>

                                        {/* Score / Seuil */}
                                        <div style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'flex-end' }}>
                                            <div style={{ width: 80 }}>
                                                <Text style={{ fontSize: 11, color: '#888', display: 'block', marginBottom: 2 }}>🧮 Score</Text>
                                                <InputNumber min={0} max={100} value={score} onChange={setScore} size="small" style={{ width: '100%' }} />
                                            </div>
                                            <div style={{ width: 80 }}>
                                                <Text style={{ fontSize: 11, color: '#888', display: 'block', marginBottom: 2 }}>🎯 Seuil</Text>
                                                <InputNumber min={0} max={100} value={threshold} onChange={setThreshold} size="small" style={{ width: '100%' }} />
                                            </div>
                                            <Button type="primary" size="small" loading={savingThreshold} icon={<SaveOutlined />}
                                                onClick={async () => { try { setSavingThreshold(true); await courseService.updateThreshold(courseId, threshold); message.success("Seuil sauvé !"); } catch (e) { message.error("Erreur."); } finally { setSavingThreshold(false); } }}
                                                style={{ borderRadius: 6, fontSize: 11, height: 24 }}>
                                                Sauver
                                            </Button>
                                        </div>

                                        <Divider style={{ margin: '6px 0 10px' }} dashed>
                                            <Text style={{ fontSize: 11, color: '#999' }}>🎨 Apparence</Text>
                                        </Divider>

                                        {/* Couleurs + Upload */}
                                        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 8 }}>
                                            <div>
                                                <Text style={{ fontSize: 10, color: '#999', display: 'block' }}>Couleur fond</Text>
                                                <Input type="color" value={backgroundColor} onChange={e => setBackgroundColor(e.target.value)}
                                                    style={{ width: 36, height: 28, padding: 1, cursor: 'pointer', borderRadius: 4 }} />
                                            </div>
                                            <div>
                                                <Text style={{ fontSize: 10, color: '#999', display: 'block' }}>Couleur texte</Text>
                                                <Input type="color" value={textColor} onChange={e => setTextColor(e.target.value)}
                                                    style={{ width: 36, height: 28, padding: 1, cursor: 'pointer', borderRadius: 4 }} />
                                            </div>
                                            <Upload showUploadList={false} customRequest={({ file }) => handleImageUpload({ file }, setLogoPreview)}>
                                                <Button icon={<PictureOutlined />} size="small" style={{ borderRadius: 6, fontSize: 11 }}>Logo</Button>
                                            </Upload>
                                            <Upload showUploadList={false} customRequest={({ file }) => handleImageUpload({ file }, setBackgroundPreview)}>
                                                <Button icon={<UploadOutlined />} size="small" style={{ borderRadius: 6, fontSize: 11 }}>Image fond</Button>
                                            </Upload>
                                            <Checkbox checked={useImageBackground} onChange={e => setUseImageBackground(e.target.checked)}>
                                                <Text style={{ fontSize: 11 }}>Activer image</Text>
                                            </Checkbox>
                                        </div>

                                        {/* Dimensions */}
                                        <Divider style={{ margin: '6px 0 8px' }} dashed>
                                            <Text style={{ fontSize: 11, color: '#999' }}><ColumnWidthOutlined /> Dimensions</Text>
                                        </Divider>
                                        <div style={{ display: 'flex', gap: 6 }}>
                                            {[
                                                { label: 'Largeur', val: badgeWidth, set: setBadgeWidth, min: 200, max: 500 },
                                                { label: 'Hauteur', val: badgeHeight, set: setBadgeHeight, min: 250, max: 700 },
                                                { label: 'Logo W', val: logoWidth, set: setLogoWidth, min: 20, max: 150 },
                                                { label: 'Logo H', val: logoHeight, set: setLogoHeight, min: 20, max: 150 },
                                            ].map((d, i) => (
                                                <div key={i} style={{ flex: 1 }}>
                                                    <Text style={{ fontSize: 9, color: '#aaa', display: 'block' }}>{d.label}</Text>
                                                    <InputNumber min={d.min} max={d.max} value={d.val} onChange={d.set} size="small" style={{ width: '100%' }} />
                                                </div>
                                            ))}
                                        </div>
                                    </Card>
                                ),
                            },
                            {
                                key: 'editor',
                                label: <span><EditOutlined /> Contenu</span>,
                                children: (
                                    <Card size="small" style={{ borderRadius: 8 }}>
                                        <Alert type="info" showIcon style={{ marginBottom: 8, fontSize: 11, padding: '6px 10px' }}
                                            message={<span style={{ fontSize: 11 }}>Variables : <code>$&#123;studentName&#125;</code> <code>$&#123;courseTitle&#125;</code> <code>$&#123;score&#125;</code> <code>$&#123;threshold&#125;</code> <code>$&#123;today&#125;</code></span>} />
                                        <Editor apiKey="v5a0uyztwrsskf09mpu56qttcqnamjr2l64oxdagfxt9zvle" value={badgeHtmlContent}
                                            init={{ height: 320, menubar: false, plugins: ["link", "lists", "paste", "code"],
                                                toolbar: "undo redo | bold italic underline | forecolor backcolor | alignleft aligncenter alignright | code",
                                                content_style: 'body { font-family: Arial, sans-serif; font-size: 12px; }' }}
                                            onEditorChange={content => setBadgeHtmlContent(content)} />
                                    </Card>
                                ),
                            },
                            {
                                key: 'test',
                                label: <span><ExperimentOutlined /> Tester</span>,
                                children: (
                                    <Card size="small" style={{ borderRadius: 8 }}>
                                        <Space direction="vertical" style={{ width: '100%' }} size={8}>
                                            <Button type="primary" icon={<TrophyOutlined />} onClick={handleGenerate} loading={loading} block
                                                style={{ borderRadius: 8, fontWeight: 600, height: 38 }}>
                                                Générer le Badge
                                            </Button>
                                            <Button icon={<SafetyCertificateOutlined />} onClick={handleAutoCheckBadge} loading={loadingAutoCheck} block
                                                style={{ borderRadius: 8, background: '#faad14', color: 'white', border: 'none', fontWeight: 600, height: 38 }}>
                                                🎯 Vérifier & Générer Auto
                                            </Button>
                                        </Space>
                                        {verificationCode && (
                                            <div style={{ marginTop: 10, padding: '8px 10px', background: '#f6ffed', borderRadius: 6, fontSize: 11, border: '1px solid #d9f7be' }}>
                                                🔗 <Text copyable={{ text: qrLink }} style={{ fontSize: 11 }}>{qrLink}</Text>
                                            </div>
                                        )}
                                    </Card>
                                ),
                            },
                        ]}
                    />
                </Col>

                {/* ═══ DROITE — APERÇU ═══ */}
                <Col span={24} lg={10}>
                    <Card size="small"
                        title={<span style={{ fontSize: 13 }}>👁️ Aperçu en direct</span>}
                        extra={<Button size="small" type="text" icon={<ReloadOutlined />} onClick={() => { setTemplateLoaded(false); message.info("Réinitialisé."); }} style={{ fontSize: 11 }}>Reset</Button>}
                        style={{ borderRadius: 8, textAlign: 'center', border: '1px solid #e8e8e8' }}>

                        <div ref={badgeRef} style={{
                            width: `${badgeWidth}px`, height: `${badgeHeight}px`,
                            background: useImageBackground && backgroundPreview ? `url(${backgroundPreview}) center/cover no-repeat` : backgroundColor,
                            color: textColor, borderRadius: 16, position: "relative",
                            boxShadow: "0 8px 24px rgba(0,0,0,0.15)", margin: '0 auto', overflow: 'hidden',
                        }}>
                            {logoPreview && <img src={logoPreview} alt="Logo" style={{ position: "absolute", top: 20, left: "50%", transform: "translateX(-50%)", width: logoWidth, height: logoHeight, borderRadius: "50%", objectFit: "cover" }} />}
                            <div style={{ padding: 20, paddingBottom: 80, height: "100%", position: "relative" }}>
                                <div dangerouslySetInnerHTML={{ __html: badgeHtmlContent }} />
                            </div>
                            <div style={{ position: "absolute", bottom: 12, right: 12, background: "rgba(255,255,255,0.15)", borderRadius: 8, padding: 6 }}>
                                <QRCodeCanvas value={qrLink} size={56} bgColor="transparent" fgColor={textColor} />
                            </div>
                            <div style={{ position: "absolute", bottom: 14, left: 14, fontSize: 10, opacity: 0.8, color: textColor }}>
                                {new Date().toLocaleDateString("fr-FR")}
                            </div>
                        </div>

                        {verificationCode && (
                            <div style={{ marginTop: 8, fontSize: 10, color: '#aaa' }}>
                                🔗 <Text copyable={{ text: qrLink }} style={{ fontSize: 10 }}>{qrLink}</Text>
                            </div>
                        )}
                    </Card>
                </Col>
            </Row>

            {/* ═══ TABLE BADGES ═══ */}
            {badges.length > 0 && (
                <Card size="small" style={{ marginTop: 12, borderRadius: 8 }}
                    title={<span style={{ fontSize: 13 }}><FireOutlined style={{ color: '#ff4d4f' }} /> Badges générés ({badges.length})</span>}>
                    <Table loading={loadingBadges} dataSource={badges} rowKey="id" size="small" pagination={{ pageSize: 5, size: 'small' }}
                        columns={[
                            { title: '👤', dataIndex: ['user', 'username'], key: 'u', width: 120, render: t => <Text strong style={{ color: '#1890ff', fontSize: 11 }}>{t || '—'}</Text> },
                            { title: 'Titre', dataIndex: 'title', key: 't', ellipsis: true, render: t => <span style={{ fontSize: 11 }}>{t}</span> },
                            { title: 'Seuil', dataIndex: 'threshold', key: 's', width: 50, render: v => <Text code style={{ fontSize: 10 }}>{v}</Text> },
                            { title: 'Date', dataIndex: 'generatedAt', key: 'd', width: 110, render: d => <span style={{ fontSize: 10 }}>{new Date(d).toLocaleDateString('fr-FR')}</span> },
                            { title: 'Code', dataIndex: 'verificationCode', key: 'c', width: 100, ellipsis: true, render: c => c ? <Text code copyable style={{ fontSize: 9 }}>{c}</Text> : '—' },
                            { title: '', key: 'a', width: 100, render: (_, b) => (
                                <Space size={2}>
                                    {b.verificationCode && <Tooltip title="Vérifier"><Button size="small" icon={<SafetyCertificateOutlined />} onClick={() => window.open(`/badge/verify/${b.verificationCode}`, '_blank')} style={{ borderRadius: 4, color: '#52c41a', borderColor: '#d9f7be' }} /></Tooltip>}
                                    <Tooltip title="Drive"><Button size="small" type="primary" icon={<EyeOutlined />} href={b.driveUrl} target="_blank" style={{ borderRadius: 4 }} /></Tooltip>
                                    <Popconfirm title="Supprimer ?" onConfirm={async () => { try { await courseService.deleteBadge(b.id); message.success("OK"); setBadges(await courseService.getBadgesByCourse(courseId)); } catch (e) { message.error("Erreur."); } }} okText="Oui" cancelText="Non">
                                        <Button danger size="small" icon={<DeleteOutlined />} style={{ borderRadius: 4 }} />
                                    </Popconfirm>
                                </Space>
                            )},
                        ]}
                    />
                </Card>
            )}
        </div>
    );
};

export default BadgeManager;